import { createSlice } from '@reduxjs/toolkit';
import { dispatch, store } from '../store';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { updateError } from './user';

const initialState = {
    isLoading: false,
    error: null,
    accounts: [],
    // Hierarchical drilldown arrays
    campaigns: [],
    adsets: [],
    ads: [],
    summary: null,
    isSyncing: false,
};

const slice = createSlice({
    name: 'metaAds',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.isLoading = action.payload;
        },
        setError(state, action) {
            state.error = action.payload;
        },
        setAccounts(state, action) {
            state.accounts = action.payload;
        },
        setCampaigns(state, action) {
            state.campaigns = action.payload;
        },
        setAdsets(state, action) {
            state.adsets = action.payload;
        },
        setAds(state, action) {
            state.ads = action.payload;
        },
        setSummary(state, action) {
            state.summary = action.payload;
        },
        setSyncing(state, action) {
            state.isSyncing = action.payload;
        },
        reset() {
            return initialState;
        },
    },
});

export const { reset: resetMetaAds } = slice.actions;

export default slice.reducer;

// --------------- Helpers ---------------

function getPracticeId() {
    const id = store.getState().practiceTreatmentList?.practiceId;
    if (!id || id === 'null' || id === 'undefined') return null;
    return id;
}

/**
 * Normalise a raw API insight row — handles both camelCase and snake_case IDs
 * and coerces all numeric fields from strings to numbers.
 */
function parseInsight(row) {
    return {
        ...row,
        campaign_id: row.campaign_id ?? row.campaignId ?? null,
        adset_id: row.adset_id ?? row.adsetId ?? null,
        ad_id: row.ad_id ?? row.adId ?? null,
        name: row.campaign_name || row.adset_name || row.ad_name || row.name || '',
        impressions: Number(row.impressions) || 0,
        clicks: Number(row.clicks) || 0,
        ctr: parseFloat(row.ctr) || 0,
        spend: parseFloat(row.spend) || 0,
        reach: Number(row.reach) || 0,
        leads: Number(row.leads) || 0,
        cost_per_lead: row.cost_per_lead != null ? parseFloat(row.cost_per_lead) : null,
    };
}

/**
 * Compute aggregated summary from campaign-level rows.
 * Reach uses max (not sum) since it counts unique people — summing inflates the figure.
 */
function buildSummary(rows) {
    const totals = rows.reduce(
        (acc, r) => {
            acc.spend += r.spend;
            acc.impressions += r.impressions;
            acc.clicks += r.clicks;
            acc.leads += r.leads;
            return acc;
        },
        { spend: 0, impressions: 0, clicks: 0, leads: 0 }
    );
    totals.reach = rows.reduce((max, r) => Math.max(max, r.reach), 0);
    totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
    totals.cost_per_lead = totals.leads > 0 ? totals.spend / totals.leads : null;
    return totals;
}

// --------------- Thunks ---------------

/**
 * Fetch connected Facebook/Meta accounts for this practice.
 */
export function fetchMetaAccounts() {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId) return;
        if (!hasFeatureAccess('facebook-integration')) return;
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/facebook-accounts/`);
            if (response.status === 200) {
                dispatch(slice.actions.setAccounts(response.data?.results || []));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch Meta accounts' }));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

/**
 * Fetch Meta ad insights at a given level (campaign | adset | ad).
 *
 * @param {string|number} accountId     – Facebook account ID
 * @param {string|number} adAccountId   – Ad account ID
 * @param {object}        params        – { level, date_from, date_to, campaign_id, adset_id }
 *
 * Results are stored in the matching slice array (campaigns / adsets / ads).
 * Campaign-level fetches also recompute the summary KPIs.
 */
export function fetchMetaInsights(accountId, adAccountId, params = {}) {
    return async () => {
        const practiceId = getPracticeId();
        if (!practiceId || !accountId || !adAccountId) return;
        if (!hasFeatureAccess('facebook-integration')) return;
        try {
            dispatch(slice.actions.setSyncing(true));
            dispatch(slice.actions.setError(null));

            const level = params.level || 'campaign';
            const qp = new URLSearchParams();
            qp.set('level', level);
            if (params.date_from) qp.set('date_from', params.date_from);
            if (params.date_to) qp.set('date_to', params.date_to);
            if (params.campaign_id) qp.set('campaign_id', params.campaign_id);
            if (params.adset_id) qp.set('adset_id', params.adset_id);

            const url = `/api/v1/practices/${practiceId}/facebook-accounts/${accountId}/ad-accounts/${adAccountId}/insights/?${qp.toString()}`;
            const response = await callApi(url);

            if (response.status === 200) {
                const raw = (response.data?.results || response.data || []).map(parseInsight);

                // Deduplicate by the most specific ID for this level — guards against
                // double-dispatch from DataGridPro row-click events or StrictMode in dev.
                const idKey = (row) => {
                    if (level === 'ad') return row.ad_id;
                    if (level === 'adset') return row.adset_id;
                    return row.campaign_id;
                };
                const seen = new Set();
                const results = raw.filter((row) => {
                    const k = idKey(row);
                    if (seen.has(k)) return false;
                    seen.add(k);
                    return true;
                });

                if (level === 'campaign') {
                    dispatch(slice.actions.setCampaigns(results));
                    dispatch(slice.actions.setSummary(buildSummary(results)));
                } else if (level === 'adset') {
                    dispatch(slice.actions.setAdsets(results));
                } else if (level === 'ad') {
                    dispatch(slice.actions.setAds(results));
                }
            } else {
                dispatch(slice.actions.setError(response.error || 'Failed to fetch Meta ad insights'));
            }
        } catch (error) {
            const msg = error.message || 'Failed to fetch Meta ad insights';
            dispatch(slice.actions.setError(msg));
            dispatch(updateError({ success: false, message: msg }));
        } finally {
            dispatch(slice.actions.setSyncing(false));
        }
    };
}
